/**
 * 使い方ガイド（画面をまたぐガイド）
 * 旧 VisitorTutorialModal（6枚のスライド）の置き換えです。実際の画面を順に移動し、各画面の実物を明るく抜いて説明します。
 * ConfirmDialog.tsx と同じく「モジュール単位のstore + useSyncExternalStore + host」の形で、host は AppNavigator に1つだけ置きます。
 *
 * 画面側が使ってよいもの:
 * - startGuidedTour({ visitorId, trigger }) … ホームだけが呼ぶ（初回の自動開始と「使い方ガイド」カード）
 * - useIsGuidedTourRunning()                … ホーム（投票系モーダルの抑止）とQRスキャン（カメラ自動起動の抑止）
 * - GuidedTourHost                          … AppNavigator だけが描画する
 * - tourAnchor(id)（shared/components/guidedTourAnchor.ts） … 指し示す要素へ付ける
 * 手順を進める・終える操作は公開しません。ガイドを進められるのはガイドのカードと、来場者自身の画面移動だけです。
 *
 * 不変条件:
 * - host は #root の中に描画する（portalしない）。Modal（緊急お知らせ・規約・「?」の説明・確認ダイアログ）が開くと
 *   #root ごと inert・aria-hidden になり、ガイドは一時停止する。これが「緊急 > 規約 > ガイド」の優先順位の実体。
 * - 既読（getTutorialSeenKey / tutorial-v2）は、最後まで進めたか・スキップ（Escape・寄り道の終了を含む）で終えたときだけ書く。
 */

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';
import webStorage from '../../../shared/services/webStorage';
import usePublicAppSettings from '../../../shared/hooks/usePublicAppSettings';
import { logError, logWarn } from '../../../shared/utils/logger';
import { buildTourAnchorSelector, type GuidedTourAnchorId } from '../../../shared/components/guidedTourAnchor';
import { resolveVisitorRouteName, useWebScreenProps } from '../../../navigation/webNavigation';
import { VISITOR_ROUTES } from '../../../navigation/routeNames';
import { getTutorialSeenKey, VISITOR_TUTORIAL_CONTENT_VERSION } from '../utils/visitorContentState';
import {
  buildGuidedTourPlan,
  GUIDED_TOUR_FIRST_STEP_ID,
  GUIDED_TOUR_STOP_TABS,
  locateGuidedTourStep,
  reconcileGuidedTourWithRoute,
  type GuidedTourStep,
  type GuidedTourStepId,
  type GuidedTourStopRoute,
} from '../utils/visitorGuidedTourPlan';
import {
  computeOverlayLayout,
  computeScrollAdjustment,
  type OverlayLayout,
  type Rect,
} from '../utils/visitorGuidedTourLayout';
import { GuidedTourCard, GuidedTourDetourBar, GuidedTourSpotlight } from './VisitorGuidedTourCard';

/** ガイドを始めたきっかけ。'auto' は未読の来場者への初回自動表示、'manual' は「使い方ガイド」カード */
export type GuidedTourTrigger = 'auto' | 'manual';

/**
 * ガイドの終わり方
 * - finished / skipped: 既読を書く
 * - abandoned: セッションが切れてログイン画面へ移った。既読にせず、ログインし直した後のホームで再び自動表示する
 * - error: ガイド自体の描画例外。既読にしないが、同じページでは自動表示を繰り返さない（例外の繰り返しを防ぐ）
 */
type GuidedTourEndReason = 'finished' | 'skipped' | 'abandoned' | 'error';

/** ガイドの状態。手順はIDだけで持ち、文言・順番は設定から毎回導出する */
type GuidedTourState =
  | Readonly<{ status: 'idle' }>
  | Readonly<{
    status: 'running';
    /** 開始ごとに増える番号。前回の実行の表示状態（例外を含む）を持ち越さないため、hostのkeyに使う */
    runId: number;
    /** 既読を書く来場者ID。空文字なら既読を書かない */
    visitorId: string;
    stepId: GuidedTourStepId;
  }>;

/** 停止中の状態 */
const IDLE_STATE: GuidedTourState = Object.freeze({ status: 'idle' });

/** 現在の状態 */
let tourState: GuidedTourState = IDLE_STATE;

/** 次に払い出す実行番号 */
let nextRunId = 1;

/**
 * このページの寿命（再読み込みまで）で自動開始した来場者ID
 * 既読の書き込みは非同期で失敗もありうるため、書き込み完了前にホームが再マウントされても・保存できない端末でも、
 * 同じページで自動開始を繰り返さないための記録。
 */
const autoOfferedVisitorIds = new Set<string>();

/** 購読者 */
const listeners = new Set<() => void>();

/**
 * 状態を差し替えて購読者へ知らせる
 * @param {GuidedTourState} nextState - 次の状態
 * @returns {void}
 */
const setTourState = (nextState: GuidedTourState): void => {
  tourState = nextState;
  listeners.forEach((listener) => listener());
};

/**
 * 状態を購読する
 * @param {() => void} listener - 変更通知
 * @returns {() => void} 購読解除
 */
const subscribeTourState = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/**
 * 現在の状態を返す
 * @returns {GuidedTourState} 状態
 */
const getTourStateSnapshot = (): GuidedTourState => tourState;

/**
 * ガイドを始める（何度呼んでも安全）
 * - 実行中なら何もしない（ホームの再マウントで既読確認が再実行されても二重に始めない）
 * - 'auto' は、このページで同じ来場者に自動開始済みなら何もしない
 * - 状態は同期で更新する。呼んだ直後の描画から「実行中」になり、投票モーダルが先に出る隙間を作らない
 * @param {{visitorId: string, trigger: GuidedTourTrigger}} input - 来場者IDときっかけ
 * @returns {void}
 */
export const startGuidedTour = (input: Readonly<{ visitorId: string; trigger: GuidedTourTrigger }>): void => {
  if (tourState.status === 'running') {
    return;
  }
  if (input.trigger === 'auto') {
    if (autoOfferedVisitorIds.has(input.visitorId)) {
      return;
    }
    autoOfferedVisitorIds.add(input.visitorId);
  }
  setTourState({
    status: 'running',
    runId: nextRunId,
    visitorId: input.visitorId,
    stepId: GUIDED_TOUR_FIRST_STEP_ID,
  });
  nextRunId += 1;
};

/**
 * ガイドの実行中かを購読する
 * ホームは投票フェス・投票結果・強制表示告知・投票状態の確認/再試行モーダルの表示条件に、
 * QRスキャンはカメラ自動起動の条件に使う。終わった瞬間にfalseへ変わるため、投票モーダルは終了直後に自動で出る。
 * @returns {boolean} 実行中ならtrue
 */
export const useIsGuidedTourRunning = (): boolean =>
  useSyncExternalStore(subscribeTourState, getTourStateSnapshot, getTourStateSnapshot).status === 'running';

/**
 * 手順を移す（host内部だけ）。停止中・同じ手順なら何もしない
 * @param {GuidedTourStepId} stepId - 次の手順ID
 * @returns {void}
 */
const moveGuidedTourTo = (stepId: GuidedTourStepId): void => {
  if (tourState.status !== 'running' || tourState.stepId === stepId) {
    return;
  }
  setTourState({ ...tourState, stepId });
};

/**
 * 既読を保存する。キーとversionは旧チュートリアルと同じ（tutorial-v2）なので、旧ガイドを見た来場者には自動表示しない
 * @param {string} visitorId - 来場者ID
 * @returns {Promise<void>}
 */
const persistTutorialSeen = async (visitorId: string): Promise<void> => {
  if (!visitorId) {
    return;
  }
  try {
    await webStorage.setItem(getTutorialSeenKey(visitorId, VISITOR_TUTORIAL_CONTENT_VERSION), '1');
  } catch (error) {
    // 保存失敗時は次のページ読み込みで再表示されるが許容（同じページでは autoOfferedVisitorIds が繰り返しを防ぐ）
    logWarn('使い方ガイドの既読状態を保存できませんでした。次回も表示されます。', {
      errorMessage: error instanceof Error ? error.message : String(error),
    });
  }
};

/**
 * ガイドを終える（host内部だけ。何度呼んでも安全）
 * @param {GuidedTourEndReason} reason - 終わり方
 * @returns {void}
 */
const endGuidedTour = (reason: GuidedTourEndReason): void => {
  if (tourState.status !== 'running') {
    return;
  }
  const { visitorId } = tourState;
  setTourState(IDLE_STATE);
  if (reason === 'abandoned') {
    autoOfferedVisitorIds.delete(visitorId);
    return;
  }
  if (reason === 'finished' || reason === 'skipped') {
    void persistTutorialSeen(visitorId);
  }
};

/**
 * テスト用: 状態と自動開始の記録を初期化する
 * @returns {void}
 */
export const resetGuidedTourForTests = (): void => {
  autoOfferedVisitorIds.clear();
  setTourState(IDLE_STATE);
};

/** 目印を探し始めてから「準備しています」の表示をやめるまで。探すこと自体は手順が変わるまで続ける */
const TARGET_SEARCH_NOTICE_MS = 4000;

/** 目印の位置がこのフレーム数だけ動かなければ、スクロールが落ち着いたとみなす */
const SCROLL_SETTLE_FRAMES = 6;

/** 本文の領域（ScreenLayout の main） */
const MAIN_CONTENT_ID = 'screen-main-content';

/**
 * host の表示状態（描画のたびに導出する。保存しない）
 * - step: 今の手順の画面にいる。暗幕・穴・カードを出す
 * - travelling: ガイドが画面を移動させている途中。カードは次の手順の文言で「画面を移動しています」
 * - detour: 立ち寄り先ではない画面にいる。暗幕を外し、戻る導線の帯だけを出す
 */
type HostMode = 'step' | 'travelling' | 'detour';

/** 目印の発見状態。key は探している手順で、手順が変わった直後の古い結果を使わないために持つ */
type TourTargetState = Readonly<{
  key: string;
  status: 'none' | 'searching' | 'absent' | 'found';
  element: HTMLElement | null;
}>;

/**
 * 使い方ガイドの表示先
 * AppNavigator で、ルートのエラー境界・Suspense の外側（兄弟）に1つだけ描画する。
 * そのため画面の切替・遅延読み込み中の待機表示・画面の描画例外でも unmount されない。
 * @returns {JSX.Element | null} 表示先
 */
export const GuidedTourHost = (): React.JSX.Element | null => {
  const state = useSyncExternalStore(subscribeTourState, getTourStateSnapshot, getTourStateSnapshot);
  if (state.status !== 'running') {
    return null;
  }
  return (
    <GuidedTourErrorBoundary key={state.runId}>
      <RunningGuidedTour stepId={state.stepId} />
    </GuidedTourErrorBoundary>
  );
};

/**
 * 実行中のガイド（進行・画面移動・目印の追跡）
 * @param {{stepId: GuidedTourStepId}} props - 現在の手順ID
 * @returns {JSX.Element} ガイド
 */
const RunningGuidedTour = ({ stepId }: Readonly<{ stepId: GuidedTourStepId }>): React.JSX.Element => {
  const { settings } = usePublicAppSettings();
  const location = useLocation();
  const currentRouteName = resolveVisitorRouteName(location.pathname);
  // 画面と同じ互換navigation。navigate は履歴を積むため、ブラウザの戻るで前の立ち寄り先へ戻れる
  const { navigation } = useWebScreenProps(currentRouteName ?? VISITOR_ROUTES.HOME);
  // ガイド自身が移動させている先。来場者自身の移動（寄り道）と区別するため、次へ・戻ると同じイベント内で立てる
  const [travellingTo, setTravellingTo] = useState<GuidedTourStopRoute | null>(null);

  const plan = useMemo(() => buildGuidedTourPlan(settings), [settings]);
  const { step, index } = locateGuidedTourStep(plan, stepId);

  const cardRef = useRef<HTMLDivElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const nextButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    // 設定の変更（準備中への切替など）で手順が消えたら、着地した手順をstoreへ書き戻す
    if (step.id !== stepId) {
      moveGuidedTourTo(step.id);
    }
  }, [step.id, stepId]);

  const latestRef = useRef({ plan, stepId: step.id, travellingTo });
  latestRef.current = { plan, stepId: step.id, travellingTo };

  // 画面が変わったときだけ突き合わせる。描画前に行い、タブで移った直後に寄り道の帯が一瞬出ないようにする
  useLayoutEffect(() => {
    const latest = latestRef.current;
    if (latest.travellingTo !== null) {
      setTravellingTo(null);
      if (latest.travellingTo === currentRouteName) {
        return;
      }
    }
    const decision = reconcileGuidedTourWithRoute(latest.plan, latest.stepId, currentRouteName);
    if (decision.kind === 'jump') {
      moveGuidedTourTo(decision.stepId);
    } else if (decision.kind === 'abandon') {
      endGuidedTour('abandoned');
    }
  }, [currentRouteName]);

  const mode: HostMode = currentRouteName === step.route
    ? 'step'
    : travellingTo === step.route
      ? 'travelling'
      : 'detour';

  /**
   * 前後の手順へ移る。画面が変わる場合は移動先を記録してから navigate する
   * @param {1 | -1} direction - 1で次へ、-1で戻る
   * @returns {void}
   */
  const handleMove = useCallback((direction: 1 | -1) => {
    if (travellingTo !== null) {
      return;
    }
    const target = plan.steps[index + direction];
    if (!target) {
      if (direction === 1) {
        endGuidedTour('finished');
      }
      return;
    }
    moveGuidedTourTo(target.id);
    if (target.route !== currentRouteName) {
      setTravellingTo(target.route);
      navigation.navigate(target.route);
    }
  }, [currentRouteName, index, navigation, plan, travellingTo]);

  /**
   * スキップする（Escape・寄り道の終了も同じ）。旧モーダルと同じく既読にする
   * @returns {void}
   */
  const handleSkip = useCallback(() => {
    endGuidedTour('skipped');
  }, []);

  /**
   * 寄り道から今の手順の画面へ戻る
   * @returns {void}
   */
  const handleResume = useCallback(() => {
    setTravellingTo(step.route);
    navigation.navigate(step.route);
  }, [navigation, step.route]);

  const target = useTourTarget(mode === 'step' ? step.anchor : null, step.id);
  const layout = useOverlayGeometry({
    step,
    mode,
    targetElement: target.status === 'found' ? target.element : null,
    cardRef,
    bodyRef,
  });
  useInertMainContent(mode === 'step');
  useNextButtonFocus({ cardRef, nextButtonRef, focusKey: `${step.id}:${mode}` });

  if (mode === 'detour') {
    return (
      <GuidedTourDetourBar
        bottom={layout?.card.bottom ?? null}
        onResume={handleResume}
        onSkip={handleSkip}
        stopLabel={GUIDED_TOUR_STOP_TABS[step.route].label}
      />
    );
  }

  return (
    <>
      <GuidedTourSpotlight layout={layout} tapThrough={step.tapThrough} />
      <GuidedTourCard
        bodyRef={bodyRef}
        cardRef={cardRef}
        isBusy={travellingTo !== null}
        layout={layout?.card ?? null}
        nextButtonRef={nextButtonRef}
        onBack={index > 0 ? () => handleMove(-1) : null}
        onNext={() => handleMove(1)}
        onSkip={handleSkip}
        statusText={mode === 'travelling'
          ? '画面を移動しています…'
          : target.status === 'searching'
            ? '画面を準備しています…'
            : ''}
        step={step}
        stops={plan.stops}
      />
    </>
  );
};

/**
 * 目印のDOM要素を探し、現れる・消えるのを追う
 * data-tour-anchor で探し、見つからなければ MutationObserver で待つ。4秒で「準備しています」をやめて記録を残すが、
 * 手順が変わるまで監視は続け、遅れて現れた（スタンプ帳の読み込み完了・遅延読み込みの画面）ら穴を開ける。
 * @param {GuidedTourAnchorId | null} anchorId - 目印ID（nullなら探さない）
 * @param {GuidedTourStepId} stepKey - 手順ID（手順が変わったら探し直す）
 * @returns {TourTargetState} 発見状態
 */
const useTourTarget = (anchorId: GuidedTourAnchorId | null, stepKey: GuidedTourStepId): TourTargetState => {
  const key = `${stepKey}:${anchorId ?? ''}`;
  const [state, setState] = useState<TourTargetState>({ key: '', status: 'none', element: null });

  useEffect(() => {
    if (!anchorId) {
      return undefined;
    }
    const selector = buildTourAnchorSelector(anchorId);
    let hasTimedOut = false;
    let lastState: TourTargetState | null = null;

    /**
     * 目印を探して状態を更新する（変化が無ければ再描画しない）
     * @returns {void}
     */
    const update = () => {
      const element = document.querySelector<HTMLElement>(selector);
      const status = element ? 'found' : hasTimedOut ? 'absent' : 'searching';
      if (lastState && lastState.status === status && lastState.element === element) {
        return;
      }
      lastState = { key, status, element };
      setState(lastState);
    };

    update();
    const observer = new MutationObserver(update);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-tour-anchor'],
    });
    const timer = setTimeout(() => {
      hasTimedOut = true;
      if (!document.querySelector(selector)) {
        logWarn('使い方ガイドの目印が見つからないため、説明だけを表示します。', { stepId: stepKey, anchorId });
      }
      update();
    }, TARGET_SEARCH_NOTICE_MS);

    return () => {
      observer.disconnect();
      clearTimeout(timer);
    };
  }, [anchorId, key, stepKey]);

  if (!anchorId) {
    return { key, status: 'none', element: null };
  }
  return state.key === key ? state : { key, status: 'searching', element: null };
};

/**
 * DOMRect を長方形へ変換する
 * @param {DOMRect} domRect - 測定値
 * @returns {Rect} 長方形
 */
const toRect = (domRect: DOMRect): Rect => ({
  top: domRect.top,
  left: domRect.left,
  width: domRect.width,
  height: domRect.height,
});

/**
 * 目印から親をたどり、縦にスクロールする最初の要素を返す
 * @param {HTMLElement} element - 目印
 * @returns {HTMLElement | null} スクロール領域
 */
const findVerticalScroller = (element: HTMLElement): HTMLElement | null => {
  let current = element.parentElement;
  while (current && current !== document.body) {
    const { overflowY } = window.getComputedStyle(current);
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return current;
    }
    current = current.parentElement;
  }
  return null;
};

/**
 * 動きを減らす設定か
 * @returns {boolean} 動きを減らす設定ならtrue
 */
const prefersReducedMotion = (): boolean =>
  typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * 暗幕・穴・カードの配置を毎フレーム測って計算し、手順ごとに一度だけ目印をスクロールで見せる
 * バナーの出入り・文字の大きさの変更・非同期の読み込み・スクロールで位置が変わるため、個別に購読せず毎フレーム測る。
 * 結果が変わったときだけ再描画する。来場者はガイド中に本文をスクロールできない（暗幕がタッチを受け止め、本文は inert）。
 * @param {Object} params - 入力
 * @param {GuidedTourStep} params.step - 手順
 * @param {HostMode} params.mode - 表示状態
 * @param {HTMLElement | null} params.targetElement - 目印
 * @param {React.RefObject<HTMLDivElement | null>} params.cardRef - カード
 * @param {React.RefObject<HTMLDivElement | null>} params.bodyRef - カードの本文
 * @returns {OverlayLayout | null} 配置（初回測定前はnull）
 */
const useOverlayGeometry = ({
  step,
  mode,
  targetElement,
  cardRef,
  bodyRef,
}: Readonly<{
  step: GuidedTourStep;
  mode: HostMode;
  targetElement: HTMLElement | null;
  cardRef: React.RefObject<HTMLDivElement | null>;
  bodyRef: React.RefObject<HTMLDivElement | null>;
}>): OverlayLayout | null => {
  const [layout, setLayout] = useState<OverlayLayout | null>(null);
  const nextTabAnchor = mode === 'step' ? step.nextTabAnchor : null;
  const stepHasAnchor = step.anchor !== null;

  useLayoutEffect(() => {
    let frameId = 0;
    let lastLayoutKey = '';
    let hasScrolled = false;
    let lastTargetTop: number | null = null;
    let stableFrames = 0;

    /**
     * 測って配置を計算し、次のフレームを予約する
     * @returns {void}
     */
    const measure = () => {
      const tabBarElement = document.querySelector<HTMLElement>(buildTourAnchorSelector('tabBar'));
      const tabBarRect = tabBarElement?.getBoundingClientRect();
      const card = cardRef.current;
      const body = bodyRef.current;
      const targetRect = targetElement?.isConnected ? toRect(targetElement.getBoundingClientRect()) : null;
      const nextTabElement = nextTabAnchor
        ? document.querySelector<HTMLElement>(buildTourAnchorSelector(nextTabAnchor))
        : null;

      if (targetRect && lastTargetTop !== null && Math.abs(targetRect.top - lastTargetTop) < 1) {
        stableFrames += 1;
      } else {
        stableFrames = 0;
      }
      lastTargetTop = targetRect?.top ?? null;

      const nextLayout = computeOverlayLayout({
        viewport: { width: window.innerWidth, height: window.innerHeight },
        tabBarTop: tabBarRect && tabBarRect.height > 0 ? tabBarRect.top : null,
        cardNaturalHeight: card && body ? card.offsetHeight - body.clientHeight + body.scrollHeight : 0,
        targetRect,
        stepHasAnchor,
        nextTabRect: nextTabElement ? toRect(nextTabElement.getBoundingClientRect()) : null,
        allowLift: hasScrolled && stableFrames >= SCROLL_SETTLE_FRAMES,
      });

      if (targetElement && targetRect && !hasScrolled) {
        hasScrolled = true;
        const scroller = findVerticalScroller(targetElement);
        if (scroller) {
          const adjustment = computeScrollAdjustment({
            targetRect,
            scrollerRect: toRect(scroller.getBoundingClientRect()),
            freeRegion: nextLayout.freeRegion,
          });
          if (Math.abs(adjustment) >= 1) {
            scroller.scrollTo({
              top: scroller.scrollTop + adjustment,
              behavior: prefersReducedMotion() ? 'auto' : 'smooth',
            });
          }
        }
      }

      const layoutKey = JSON.stringify(nextLayout, (_key, value) => (typeof value === 'number' ? Math.round(value) : value));
      if (layoutKey !== lastLayoutKey) {
        lastLayoutKey = layoutKey;
        setLayout(nextLayout);
      }
      frameId = window.requestAnimationFrame(measure);
    };

    measure();
    return () => window.cancelAnimationFrame(frameId);
  }, [bodyRef, cardRef, nextTabAnchor, stepHasAnchor, targetElement]);

  return layout;
};

/**
 * 手順を表示している間、表示中の画面の本文（ScreenLayout の main）を inert にする
 * 暗幕はタップを受け止めるが、キーボードとスクリーンリーダーは暗幕を素通りするため、見た目と操作できる範囲を揃える。
 * ヘッダー（「?」）と下部タブは本文の外なので操作できるまま。画面が切り替わると要素も変わるため付け直す。
 * @param {boolean} active - 付けるか
 * @returns {void}
 */
const useInertMainContent = (active: boolean): void => {
  useLayoutEffect(() => {
    if (!active) {
      return undefined;
    }
    let markedElement: HTMLElement | null = null;

    /**
     * 今の本文へ付け、前の本文から外す
     * @returns {void}
     */
    const apply = () => {
      const mainElement = document.getElementById(MAIN_CONTENT_ID);
      if (mainElement === markedElement) {
        return;
      }
      markedElement?.removeAttribute('inert');
      markedElement = null;
      if (mainElement && !mainElement.hasAttribute('inert')) {
        mainElement.setAttribute('inert', '');
        markedElement = mainElement;
      }
    };

    apply();
    const observer = new MutationObserver(apply);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      markedElement?.removeAttribute('inert');
    };
  }, [active]);
};

/**
 * 次へボタンへfocusを置く
 * 開始時に置き、以後は手順が変わっても動かさない（Enterの連打で進める）。画面の移動中に次へが無効になった後や、
 * Modal が閉じて #root の inert が外れた後に focus が body へ落ちていたら、次へへ戻す。
 * 終了時は、focus がガイドの中にあれば表示中の画面の本文へ移す。
 * @param {Object} params - 入力
 * @param {React.RefObject<HTMLDivElement | null>} params.cardRef - カード
 * @param {React.RefObject<HTMLButtonElement | null>} params.nextButtonRef - 次へボタン
 * @param {string} params.focusKey - 手順と表示状態（変わったときに focus の落ちを確かめる）
 * @returns {void}
 */
const useNextButtonFocus = ({
  cardRef,
  nextButtonRef,
  focusKey,
}: Readonly<{
  cardRef: React.RefObject<HTMLDivElement | null>;
  nextButtonRef: React.RefObject<HTMLButtonElement | null>;
  focusKey: string;
}>): void => {
  useLayoutEffect(() => {
    nextButtonRef.current?.focus();
    const container = cardRef.current?.closest('body > *');
    const observer = container
      ? new MutationObserver(() => {
        if (!container.hasAttribute('inert') && isFocusLost()) {
          nextButtonRef.current?.focus();
        }
      })
      : null;
    observer?.observe(container as Element, { attributes: true, attributeFilter: ['inert'] });

    return () => {
      observer?.disconnect();
      const card = cardRef.current;
      if (isFocusLost() || (card && card.contains(document.activeElement))) {
        document.getElementById(MAIN_CONTENT_ID)?.focus();
      }
    };
  }, [cardRef, nextButtonRef]);

  useEffect(() => {
    if (isFocusLost()) {
      nextButtonRef.current?.focus();
    }
  }, [focusKey, nextButtonRef]);
};

/**
 * focus がどこにも無い（body に落ちている）か
 * @returns {boolean} 落ちていればtrue
 */
const isFocusLost = (): boolean => !document.activeElement || document.activeElement === document.body;

/** ガイド自体の例外を握るエラー境界のプロパティ */
type GuidedTourErrorBoundaryProps = { children: React.ReactNode };

/**
 * ガイド自体の描画例外でアプリ全体を止めないためのエラー境界
 * 例外時はガイドを終えて何も描画しない。既読にはしないが、同じページで自動表示は繰り返さない。
 */
class GuidedTourErrorBoundary extends React.Component<GuidedTourErrorBoundaryProps, { hasError: boolean }> {
  state = { hasError: false };

  /**
   * 例外を受けて非表示にする
   * @returns {{hasError: boolean}} 次の状態
   */
  static getDerivedStateFromError() {
    return { hasError: true };
  }

  /**
   * 例外を記録し、ガイドを終える
   * @param {unknown} error - 例外
   * @returns {void}
   */
  componentDidCatch(error: unknown) {
    logError('使い方ガイドの表示で例外が発生したため、ガイドを終了しました。', {
      errorMessage: error instanceof Error ? error.message : String(error),
    });
    endGuidedTour('error');
  }

  /**
   * 子要素を描画する
   * @returns {React.ReactNode} 子要素
   */
  render() {
    return this.state.hasError ? null : this.props.children;
  }
}
